import { MatchStatus } from "./../../service/index";
import { EVENTS, MESSAGES } from "./constants";
import { LANGUAGE } from "_constants";
import { FoulIcon, TimeoutIcon } from "../../assets";

export const checkIsAway = (status: MatchStatus) => {
  if (!status.sc || status.sc.length === 4) return undefined;
  if (status.sc.length === 5) {
    if (status.sc.startsWith("2")) return true;
    else if (status.sc.startsWith("1")) return false;
    else if (status.sc.startsWith("0")) return undefined;
  }
};

export type BasketballTabKey = "narration" | "stats" | "match";

export const BASKETBALL_TABS: { key: BasketballTabKey; label: string }[] = [
  { key: "narration", label: "Narration" },
  { key: "stats", label: "Statistics" },
  { key: "match", label: "Match" },
];

export const getBasketballTabIndex = (key: BasketballTabKey): number =>
  BASKETBALL_TABS.findIndex((t) => t.key === key);

export const getBasketballTabKeyByIndex = (i: number): BasketballTabKey =>
  BASKETBALL_TABS[i]?.key ?? "narration";
export const getActiveTeamName = (status: MatchStatus) => {
  const isAway = checkIsAway(status);

  const homeName =
    (status as any)?.T1?.n ??
    (status as any)?.t1?.n ??
    (status as any)?.T1n ??
    null;
  const awayName =
    (status as any)?.T2?.n ??
    (status as any)?.t2?.n ??
    (status as any)?.T2n ??
    null;

  if (isAway === undefined) return null;
  return isAway ? awayName : homeName;
};

export const getDescription = (status: MatchStatus) => {
  if (status.nsc === "1078") {
    const [a, b] = status.sts
      ?.split("|")
      .find((x) => x.startsWith("INFO="))
      ?.split("INFO=")[1]
      .split(":") || [undefined, undefined];
    if (a && b) return `${a} of ${b}`;
  }
};

export const getIcon = (normalizedStatusCode?: string) => {
  if (!normalizedStatusCode) return undefined;
  switch (normalizedStatusCode) {
    case EVENTS["Time Out"]:
      return TimeoutIcon;
    case EVENTS["Foul"]:
      return FoulIcon;
  }
};

export const processStatus = (status: MatchStatus, language: LANGUAGE) => {
  const isAway = checkIsAway(status);
  const teamName = getActiveTeamName(status);
  const normalizedStatusCode = status.sc?.substr(
    status?.sc?.length - 4,
    4
  ) as EVENTS;

  const messageDict = MESSAGES[language] || EVENTS;

  const message = normalizedStatusCode
    ? messageDict[normalizedStatusCode]?.toUpperCase?.()
    : undefined;
  const description = getDescription(status);
  const icon = getIcon(normalizedStatusCode);

  return {
    isAway,
    teamName,
    normalizedStatusCode,
    message,
    description,
    icon,
  };
};

// ======================
// STATS PARSING HELPERS
// ======================

export const extract = (splitStatus: any, keyOrKeys: string | string[]) => {
  if (!splitStatus || !Array.isArray(splitStatus)) {
    return [0, 0] as [number, number];
  }
  const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
  const item = splitStatus.find((status: string) => {
    if (!status || typeof status !== "string") return false;
    return keys.some((k) => status.startsWith(k));
  });
  if (!item) return [0, 0] as [number, number];
  const dataString = item.split("=")[1];
  if (!dataString) return [0, 0] as [number, number];
  const toNum = (v: string) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  };
  const data = dataString.split(":").map(toNum);
  if (!(data && data.length === 2)) return [0, 0] as [number, number];
  return [data[0], data[1]] as [number, number];
};

export const extractFirstNonZero = (splitStatus: any, keys: string[]) => {
  for (const k of keys) {
    const v = extract(splitStatus, k);
    if ((v?.[0] ?? 0) !== 0 || (v?.[1] ?? 0) !== 0) return v;
  }
  return extract(splitStatus, keys[0]);
};

export const ensurePair = (arr: any): [number, number] =>
  Array.isArray(arr) && arr.length === 2
    ? [Number(arr[0]) || 0, Number(arr[1]) || 0]
    : [0, 0];
